import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  CheckBox,
  COLORS,
  Avatar,
  AVATAR_BACKGROUND,
  AVATAR_TYPES,
  SIZE,
  BodySecondary,
} from '@bikdotai/bik-component-library';
import {
  PopupWrapper,
  PopupTabsContainer,
  PopupTab,
  PopupSearchWrapper,
  PopupSearchInput,
  SelectAllRow,
  SelectAllContent,
  PopupList,
  PopupListItem,
  PopupFooter,
  EmptyResults,
} from './EditMacroModal.style';

export interface TeamI {
  id: number;
  name: string;
}

export interface UserI {
  id: number;
  name: string;
  email?: string;
}

export enum PopupTabType {
  TEAM = 'TEAM',
  STAFF = 'STAFF',
}

export interface MultiSelectTeamAgentPopupProps {
  selectedTeamIds: number[];
  selectedAgentIds: number[];
  allTeams: TeamI[];
  allUsers: UserI[];
  onApply: (teamIds: number[], agentIds: number[]) => void;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLDivElement>;
}

const MultiSelectTeamAgentPopup: React.FC<MultiSelectTeamAgentPopupProps> = ({
  selectedTeamIds,
  selectedAgentIds,
  allTeams,
  allUsers,
  onApply,
  onClose,
  triggerRef,
}) => {
  const [activeTab, setActiveTab] = useState<PopupTabType>(PopupTabType.TEAM);
  const [search, setSearch] = useState('');
  const [tempTeamIds, setTempTeamIds] = useState<number[]>(selectedTeamIds);
  const [tempAgentIds, setTempAgentIds] = useState<number[]>(selectedAgentIds);
  const popupRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top?: number; bottom?: number; left: number } | null>(null);

  // Position the popup relative to the trigger button
  useEffect(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const popupHeight = 450;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < popupHeight && rect.top > popupHeight;
    setPosition(
      openUpward
        ? { bottom: window.innerHeight - rect.top + 8, left: rect.left }
        : { top: rect.bottom + 8, left: rect.left }
    );
  }, [triggerRef]);

  // Reset temp state when popup reopens with new props
  useEffect(() => {
    setTempTeamIds(selectedTeamIds);
    setTempAgentIds(selectedAgentIds);
    setSearch('');
  }, [selectedTeamIds, selectedAgentIds]);

  const items = activeTab === PopupTabType.TEAM ? allTeams : allUsers;
  const filteredItems = items.filter((item) =>
    item.name.toLowerCase().includes(search.toLowerCase())
  );

  const selectedIds = activeTab === PopupTabType.TEAM ? tempTeamIds : tempAgentIds;
  const allIds = filteredItems.map((item) => item.id);
  const isAllSelected = filteredItems.length > 0 && allIds.every((id) => selectedIds.includes(id));

  const handleToggle = (id: number) => {
    if (activeTab === PopupTabType.TEAM) {
      setTempTeamIds((prev) =>
        prev.includes(id) ? prev.filter((tid) => tid !== id) : [...prev, id]
      );
    } else {
      setTempAgentIds((prev) =>
        prev.includes(id) ? prev.filter((id2) => id2 !== id) : [...prev, id]
      );
    }
  };

  const handleSelectAll = () => {
    if (activeTab === PopupTabType.TEAM) {
      if (isAllSelected) {
        setTempTeamIds((prev) => prev.filter((id) => !allIds.includes(id)));
      } else {
        setTempTeamIds((prev) => [...new Set([...prev, ...allIds])]);
      }
    } else {
      if (isAllSelected) {
        setTempAgentIds((prev) => prev.filter((id) => !allIds.includes(id)));
      } else {
        setTempAgentIds((prev) => [...new Set([...prev, ...allIds])]);
      }
    }
  };

  return (
    <PopupWrapper ref={popupRef} style={position ? { top: position.top, bottom: position.bottom, left: position.left } : { visibility: 'hidden' }}>
      {/* Tabs */}
      <PopupTabsContainer>
        <PopupTab
          $active={activeTab === PopupTabType.TEAM}
          onClick={() => setActiveTab(PopupTabType.TEAM)}
        >
          Team ({allTeams.length})
        </PopupTab>
        <PopupTab
          $active={activeTab === PopupTabType.STAFF}
          onClick={() => setActiveTab(PopupTabType.STAFF)}
        >
          Staff ({allUsers.length})
        </PopupTab>
      </PopupTabsContainer>

      {/* Search */}
      <PopupSearchWrapper>
        <PopupSearchInput
          type="text"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </PopupSearchWrapper>

      {/* Select All */}
      {filteredItems.length > 0 && (
        <SelectAllRow>
          <SelectAllContent onClick={handleSelectAll}>
            <div onClick={(e) => e.stopPropagation()}>
              <CheckBox isChecked={isAllSelected} onValueChange={handleSelectAll} />
            </div>
            <BodySecondary color={COLORS.content.primary}>
              Select All
            </BodySecondary>
          </SelectAllContent>
        </SelectAllRow>
      )}

      {/* List */}
      <PopupList>
        {filteredItems.map((item) => {
          const isSelected = selectedIds.includes(item.id);
          return (
            <PopupListItem
              key={item.id}
              $selected={isSelected}
              onClick={() => handleToggle(item.id)}
            >
              <div onClick={(e) => e.stopPropagation()}>
                <CheckBox isChecked={isSelected} onValueChange={() => handleToggle(item.id)} />
              </div>
              <Avatar
                name={item.name}
                size={SIZE.SMALL}
                type={AVATAR_TYPES.INITIALS}
                background={AVATAR_BACKGROUND.DARK}
              />
              <BodySecondary
                color={isSelected ? COLORS.content.positive : COLORS.content.primary}
                style={{
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.name}
              </BodySecondary>
            </PopupListItem>
          );
        })}
        {filteredItems.length === 0 && (
          <EmptyResults>No results found</EmptyResults>
        )}
      </PopupList>

      {/* Footer with Apply button */}
      <PopupFooter>
        <Button
          buttonText="Apply"
          buttonType="primary"
          size="small"
          onClick={() => {
            onApply(tempTeamIds, tempAgentIds);
            onClose();
          }}
        />
      </PopupFooter>
    </PopupWrapper>
  );
};

export default MultiSelectTeamAgentPopup;
